import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  defaultIfEmpty,
  first,
  firstValueFrom,
  map,
  Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  tap,
} from 'rxjs';
import { Filter } from 'src/app/models/common/filter';
import { FilterConfig } from '../model/filter-config.interface';
import { FilterTypeEnum } from 'src/app/models/enums/filter-type-enum';
import { UserService } from 'src/app/data/user.service';
import { FilterPresetDto } from 'src/app/models/dtos/filter-preset-dto';
import { FilterType } from '../enums/filter-type.enum';
import { FilterOperators } from 'src/app/common/enums';
import { DictionaryItem } from 'src/app/models/DictionaryItem';

export abstract class FilterService {
  private filtersPresetSubject = new BehaviorSubject<FilterPresetDto>(null);
  private selectPresetSubject = new BehaviorSubject<number>(null);
  private fetchPresest$ = new Subject<void>();
  public filterPresets$ = combineLatest([
    this.filtersPresetSubject.asObservable().pipe(startWith(null)),
    this.fetchPresest$.asObservable().pipe(
      startWith(null),
      switchMap(() => this.userService.getFilterPresets(this.filterType)),
    ),
  ])
    .pipe(
      map(([local, fromDb]) => {
        if (local) {
          const index = fromDb.findIndex((p) => p.Name === local.Name);

          if (index === -1) {
            return [local, ...fromDb];
          }

          fromDb[index] = local;
        }

        return fromDb;
      }),
    )
    .pipe(
      switchMap((presets) =>
        this.selectPresetSubject
          .asObservable()
          .pipe(startWith(undefined))
          .pipe(map((presetId) => (presetId ? presets.map((preset) => ({ ...preset, IsSelected: preset.Id === presetId })) : presets))),
      ),
    )
    .pipe(debounceTime(0))
    .pipe(shareReplay(1));

  public selectedFilterPreset$ = this.filterPresets$.pipe(map((fps) => fps.find((fps) => fps.IsSelected))).pipe(shareReplay(1));

  public selectedFilterPresetId$ = this.selectedFilterPreset$.pipe(map((fp) => fp?.Id));

  public filtersPreset$ = this.selectedFilterPreset$.pipe(map((filterPreset) => filterPreset?.Object)).pipe(defaultIfEmpty({}));

  public filters$: Observable<Filter[]> = this.filtersPreset$.pipe(
    map((values) => this.buildFilterArray(values, this.config)),
    shareReplay(1),
  );

  private configSubject = new BehaviorSubject<FilterConfig[]>([]);

  public config$ = this.configSubject.asObservable();

  public async setFiltersPreset(value: {}) {
    const newPreset: FilterPresetDto = {
      Id: 0,
      FilterTypeId: this.filterType,
      IsSelected: true,
      Name: this.defaultFilterPresetName,
    } as FilterPresetDto;

    await firstValueFrom(
      this.filterPresets$
        .pipe(first())
        .pipe(map((presets) => presets.find((p) => p.Name === this.defaultFilterPresetName) ?? newPreset))
        .pipe(map((preset) => ({ ...preset, Object: value })))
        .pipe(tap((preset) => this.filtersPresetSubject.next(preset)))
        .pipe(
          switchMap((preset) =>
            !preset.Id
              ? this.userService
                .addFilterPreset({
                  FilterTypeId: preset.FilterTypeId,
                  Name: preset.Name,
                  FilterPresetObject: preset.Object,
                })
                .pipe(tap((id) => (preset.Id = id)))
                .pipe(map(() => preset))
              : this.userService
                .updateFilterPreset(preset.Id, {
                  Name: preset.Name,
                  FilterPresetObject: preset.Object,
                })
                .pipe(map(() => preset)),
          ),
        )
        .pipe(tap((preset) => this.setActiveFilterPreset(preset.Id))),
    );
  }

  public get filtersPreset(): FilterPresetDto {
    return this.filtersPresetSubject.value;
  }

  protected set config(value: FilterConfig[]) {
    this.configSubject.next(value);
  }
  public get config(): FilterConfig[] {
    return this.configSubject.value;
  }

  private readonly defaultFilterPresetName = 'default';

  constructor(protected readonly filterType: FilterTypeEnum, protected userService: UserService) { }

  resetFilters(): Observable<void> {
    return this.selectedFilterPreset$.pipe(first()).pipe(
      switchMap((preset) =>
        preset
          ? this.userService
            .deselectFilterPreset(preset.Id)
            .pipe(tap(() => this.filtersPresetSubject.next(null)))
            .pipe(tap(() => this.selectPresetSubject.next(null)))
            .pipe(tap(() => this.fetchPresest$.next()))
          : of(null),
      ),
    );
  }

  saveFilters(filterName = this.defaultFilterPresetName): Observable<any> {
    return this.selectedFilterPreset$
      .pipe(first())
      .pipe(map((preset) => ({ ...preset, Name: filterName })))
      .pipe(tap((preset) => this.filtersPresetSubject.next(preset)))
      .pipe(switchMap((preset) => this.userService.updateFilterPreset(preset.Id, { Name: preset.Name, FilterPresetObject: preset.Object })))
      .pipe(tap(() => this.fetchPresest$.next()));
  }

  unsaveFilters(): Observable<void> {
    return this.selectedFilterPreset$.pipe(first()).pipe(
      switchMap((preset) =>
        preset
          ? this.userService
            .deleteFilterPreset(preset.Id)
            .pipe(tap(() => this.filtersPresetSubject.next(null)))
            .pipe(tap(() => this.selectPresetSubject.next(null)))
            .pipe(tap(() => this.fetchPresest$.next()))
          : of(null),
      ),
    );
  }

  setActiveFilterPreset(id: number): Observable<void> {
    this.selectPresetSubject.next(id);
    return this.userService.selectFilterPreset(id);
  }

  private buildFilterArray(values: any, filterConfigs: FilterConfig[]): Filter[] {
    const filters: Filter[] = [];

    for (const config of filterConfigs) {
      if (!config.property) continue;

      if (config.type === FilterType.DateRange && Array.isArray(config.name)) {
        const fromName = config.name.find((name) => name.includes('From'));
        if (fromName && values && !!values[fromName]) {
          filters.push({
            Key: config.property,
            Operator: FilterOperators.DateGreaterThanOrEqual,
            Value: [values[fromName]],
          } as Filter);
        }

        const toName = config.name.find((name) => name.includes('To'));
        if (toName && values && !!values[toName]) {
          filters.push({
            Key: config.property,
            Operator: FilterOperators.DateLessThanOrEqual,
            Value: [values[toName]],
          } as Filter);
        }
      } else if (!Array.isArray(config.name)) {
        const configName = config.name;

        if (values && (!!values[configName] || typeof values[configName] === 'boolean')) {
          filters.push({
            Key: config.property,
            Operator: config.operator,
            Value: Array.isArray(values[configName]) ? values[configName] : [values[configName]],
          } as Filter);
        }
      }
    }

    return filters.filter((f) => !!f.Value && !!f.Operator);
  }

  booleanFilter(): Observable<DictionaryItem[]> {
    const options: DictionaryItem[] = [
      { Id: true as any, Name: 'Yes' },
      { Id: false as any, Name: 'No' }
    ];

    return of(options);
  }

  dateRangeFields(baseName: string): string[] {
    return [`${baseName}From`, `${baseName}To`];
  }
}
